/**
 * One clarifying question, rendered inline in the conversation.
 *
 * The card is answered exactly once. After that it stays on screen in a read-only
 * state showing what was chosen, because the transcript is a record of what
 * happened and a card that disappears leaves the following answer unexplained.
 *
 * Answering submits an ordinary turn carrying `answerStep`, so the runtime can
 * tell a current answer from one sent by a stale tab after a reload.
 */
import { useState } from "react";
import { ArrowRight, Check, Pencil } from "lucide-react";
import type { ClarifyingQuestionEvent } from "../../services/agui";
import { Markdown } from "../../components/Markdown";
import { labelSectionKinds } from "../../services/displayText";

/** Sentinel the runtime understands as "no preference". */
export const SKIP_TOKEN = "__skip__";

/**
 * Splits a question into the line it asks and the detail beneath it.
 *
 * Most questions are one line and belong in the heading. But an agent asking for
 * approval of something it built sends the whole thing -- a numbered document
 * structure, several lines of it, in Markdown. Rendered as a heading that
 * collapsed to a single run-on line with its asterisks showing, which is the
 * least readable form of the one thing the user is being asked to approve.
 */
function splitQuestion(summary: string): { lead: string; detail: string } {
  const trimmed = summary.trim();
  const breakAt = trimmed.indexOf("\n");
  if (breakAt === -1) return { lead: trimmed, detail: "" };
  const first = trimmed.slice(0, breakAt).trim();
  // A question that opens straight into its list has no sentence to promote, and
  // taking the first line anyway made section one the card's heading. The options
  // say what is being asked, so the card carries the list alone.
  if (/^(\d+[.)]|[-*])\s+/.test(first)) return { lead: "", detail: trimmed };
  return { lead: first, detail: trimmed.slice(breakAt + 1).trim() };
}

interface Props {
  question: ClarifyingQuestionEvent;
  /** The answer already given, when this card has been answered. */
  answer?: string;
  /** True while a turn is in flight, so the card cannot be submitted twice. */
  busy?: boolean;
  onAnswer: (question: ClarifyingQuestionEvent, answer: string) => void;
  onDismiss?: (questionId: string) => void;
}

export default function QuestionCard({
  question,
  answer,
  busy = false,
  onAnswer,
  onDismiss,
}: Props) {
  const [draft, setDraft] = useState("");
  const [typing, setTyping] = useState(question.options.length === 0);
  const answered = answer !== undefined;
  const { lead, detail } = splitQuestion(labelSectionKinds(question.summary));

  const submit = (value: string) => {
    const trimmed = value.trim();
    if (!trimmed || answered || busy) return;
    onAnswer(question, trimmed);
  };

  return (
    <section
      className={`question-card${answered ? " answered" : ""}`}
      aria-label={question.summary}
      // Announced politely so a screen reader hears the question when it
      // arrives, without interrupting the reply that introduced it.
      aria-live={answered ? undefined : "polite"}
    >
      <header className="question-card-head">
        {lead ? <h3>{lead}</h3> : <span className="question-card-lead-gap" />}
        <div className="question-card-meta">
          {question.position && question.total ? (
            <span className="question-pager">
              {question.position} of {question.total}
            </span>
          ) : null}
          {!answered && onDismiss ? (
            <button
              type="button"
              className="question-dismiss"
              aria-label="Dismiss this question"
              onClick={() => onDismiss(question.questionId)}
            >
              ×
            </button>
          ) : null}
        </div>
      </header>

      {/* What is being asked about, when the question carries more than a line:
          a proposed structure, rendered as the list the agent wrote rather than
          as one paragraph with its markup showing. */}
      {detail && (
        <div className="question-card-detail">
          <Markdown text={detail} />
        </div>
      )}

      <ol className="question-options">
        {question.options.map((option, index) => {
          const chosen = answered && answer === option.label;
          return (
            <li key={option.id}>
              <button
                type="button"
                className={`question-option${chosen ? " chosen" : ""}`}
                disabled={answered || busy}
                onClick={() => submit(option.label)}
              >
                <span className="question-option-index" aria-hidden="true">
                  {chosen ? <Check size={13} /> : index + 1}
                </span>
                <span className="question-option-body">
                  <span className="question-option-label">{option.label}</span>
                  {option.description ? (
                    <span className="question-option-description">
                      {option.description}
                    </span>
                  ) : null}
                </span>
              </button>
            </li>
          );
        })}
      </ol>

      {answered ? (
        // Free text and Skip are replaced by the outcome, so the card reads as
        // a settled decision rather than an input still waiting.
        question.options.some((option) => option.label === answer) ? null : (
          <p className="question-answered-note">
            <Check size={13} aria-hidden="true" />
            {answer === SKIP_TOKEN ? "Skipped" : answer}
          </p>
        )
      ) : (
        <footer className="question-card-foot">
          {question.freeText ? (
            typing ? (
              <form
                className="question-freetext"
                onSubmit={(event) => {
                  event.preventDefault();
                  submit(draft);
                }}
              >
                <Pencil size={14} aria-hidden="true" />
                <input
                  autoFocus
                  value={draft}
                  disabled={busy}
                  placeholder={question.freeTextLabel}
                  aria-label={question.freeTextLabel}
                  onChange={(event) => setDraft(event.target.value)}
                />
                <button
                  type="submit"
                  className="question-freetext-submit"
                  disabled={!draft.trim() || busy}
                  aria-label="Submit answer"
                >
                  <ArrowRight size={15} />
                </button>
              </form>
            ) : (
              <button
                type="button"
                className="question-freetext-open"
                disabled={busy}
                onClick={() => setTyping(true)}
              >
                <Pencil size={14} aria-hidden="true" />
                {question.freeTextLabel}
              </button>
            )
          ) : (
            <span />
          )}
          {question.skippable ? (
            <button
              type="button"
              className="question-skip"
              disabled={busy}
              onClick={() => onAnswer(question, SKIP_TOKEN)}
            >
              Skip
            </button>
          ) : null}
        </footer>
      )}
    </section>
  );
}
